import { useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { sendPasswordResetCodeAsync } from "@/store/redux/slices/auth/auth_slice";
import Input from "@/components/common/inputs/Input";
import { useLoader } from "@/hooks/LoaderProvider";
import Button from "@/components/common/Button";
import { ArrowRightOutlined } from "@/config/antd";
import Copyright from "@/components/layout/Footer/Copyright";
import Success from "@/components/common/alerts/Success";
import WebIcon from "@/components/common/Web_Icons";
import Error from "@/components/common/alerts/Error";
import AuthHeader from "@/components/layout/Header/Auth_Header";

const PasswordRecovery = () => {
  const { showLoader, hideLoader } = useLoader();
  const [email, setEmail] = useState("");
  // userID from send-code-to-email — needed by forgot-password on the next screen.
  const [recoveryUserID, setRecoveryUserID] = useState(null);
  const [isSuccessVisible, setIsSuccessVisible] = useState(false);
  const [isErrorVisible, setIsErrorVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleEmailChange = (e) => setEmail(e.target.value);

  const handleProceed = useCallback(async () => {
    showLoader();
    try {
      const resultAction = await dispatch(
        sendPasswordResetCodeAsync(email.toLowerCase())
      );

      if (sendPasswordResetCodeAsync.fulfilled.match(resultAction)) {
        // Response shape: { message, data: { userID, ... } }
        const userID =
          resultAction.payload?.data?.userID ||
          resultAction.payload?.userID ||
          null;
        setRecoveryUserID(userID);
        setIsSuccessVisible(true);
      } else {
        setErrorMessage(resultAction.payload || "Failed to send recovery code");
        setIsErrorVisible(true);
      }
    } catch {
      setErrorMessage("An error occurred while sending the recovery code");
      setIsErrorVisible(true);
    } finally {
      hideLoader();
    }
  }, [email, dispatch, showLoader, hideLoader]);

  const handleCloseError = () => {
    setIsErrorVisible(false);
  };

  const handleCloseSuccess = () => {
    setIsSuccessVisible(false);
  };

  const handleSuccessContinue = () => {
    showLoader();
    setTimeout(() => {
      // Pass userID via route state; Redux also keeps passwordResetUserID as fallback.
      navigate("/password-reset", { state: { userID: recoveryUserID } });
      hideLoader();
    }, 2000);
  };

  return (
    <div className="flex flex-col h-screen bg-white">
      {/* Top section */}
      <AuthHeader
        logoName="CMSLogo"
        logoSize="large"
        title="Have an existing Veegil SSO account?"
        titlePath="/signin"
        justifyBetween={true}
        className="px-4 py-2 md:px-6"
      />

      {/* Middle section */}
      <div className="flex flex-col px-4 md:px-6 max-w-md mx-auto w-full mt-8">
        <h1 className="heading-large whitespace-nowrap text-left text-primary mb-4">
          Account Recovery
        </h1>
        <p className="text-small text-left text-darkGrey mb-6 w-full">
          You can recover your account on this page by entering your email
          address below.
        </p>

        <div className="mb-4">
          <Input
            label="Email Address"
            placeholder="Enter your email"
            icon={<WebIcon icon="Message" size="small" />}
            type="email"
            value={email}
            onChange={handleEmailChange}
          />
        </div>

        <div>
          <Button
            text="Proceed"
            icon={<ArrowRightOutlined />}
            onClick={handleProceed}
            isActive={email.trim() !== ""}
            color="darkBlue"
            className={`w-full ${
              email.trim() === ""
                ? "bg-darkBlue text-primary"
                : "bg-primary text-white"
            }`}
          />
        </div>

        <div className="mt-8">
          <Copyright />
        </div>
      </div>

      <Success
        isVisible={isSuccessVisible}
        onClose={handleCloseSuccess}
        header="Recovery Message Sent"
        paragraph="We sent you a recovery message to guide you on how to reset your password."
        buttonText="Continue"
        onButtonClick={handleSuccessContinue}
      />

      <Error
        isVisible={isErrorVisible}
        onClose={handleCloseError}
        header="Failed to Send Recovery Code"
        paragraph={errorMessage}
        buttonText="Try Again"
        recoveryPath="/account-recovery/password-recovery"
      />
    </div>
  );
};

export default PasswordRecovery;
